/**
 * The Capacity_Period editor (task 13.1, R3.1, R3.2, R3.3, R3.6, R7.8; UI spec
 * §5.3).
 *
 * ## The contents of a dialog, not a dialog
 *
 * The page mounts this inside an {@link EditorDialog} rather than as a column
 * beside the register: an entry is a decision about one named period, and the
 * figures being replaced are the ones in the row behind it. That shared dialog owns
 * the modal behaviour, the card, the heading and the note (UI spec §3.11); what
 * belongs here is the findings, the field grid, the derived-capacity preview and the
 * action row. The heading and note it renders come from
 * {@link capacityEditorTitleKey} and {@link capacityEditorNoteKey}, so the
 * three-way create/edit/copy rule stays in this module beside the `mode` semantics
 * it depends on.
 *
 * ## Two modes, because editing a period is not the same as adding one
 *
 * R3.3 describes an edit as a change to "an existing Capacity_Period's headcount,
 * workdays, OOF days, productive-hours assumption or availability factor" — the
 * figures, never the FF role, Region or effective period, which R3.1 makes the
 * record's identity. The panel therefore has two modes:
 *
 * - `create` offers every field, including the scope. This is how a period that
 *   does not exist yet gets recorded.
 * - `edit` shows the scope as read-only context and offers only R3.3's figures,
 *   so adjusting a headcount cannot silently re-point the period at another role,
 *   Region or date range and leave the original standing as a duplicate.
 *
 * Neither mode ever updates a stored row: an edit is recorded as the next version
 * of its scope and the previous versions are retained unchanged (R3.3). The change
 * reason is always blank on seeding — it describes this change, not the previous
 * one.
 *
 * ## Reseeding is a remount, not an effect
 *
 * The page gives this component a `key` that changes whenever the seed changes (a
 * row's "new version", the "add" control, or a completed save). React then
 * remounts it and the `useState` initialiser is the only seeding path. The
 * alternative — an effect watching `basedOn`, `defaultRegionId` and
 * `defaultFfRole` — would also fire when the reader merely changed the *filters*
 * those defaults derive from, wiping half-typed input.
 *
 * ## The warnings are the server's, not ours
 *
 * R3.6: a submission that would trigger recalculation is refused until the writer
 * acknowledges it. The same protocol carries the capacity-shortfall warning — the
 * one raised when the submitted figures leave the period unable to cover the
 * demand already planned into it, which is a warning and not a block because
 * recording reduced capacity is legitimate and the resulting over-capacity state
 * is what the utilization screens report.
 *
 * Both decisions belong to the service, so this form submits without an
 * acknowledgement first and lets the server answer. When the answer is a
 * `validation_failed` carrying warning findings, the panel shows them and asks for
 * confirmation; confirming re-submits echoing back the exact `ruleId`s the server
 * named — all of them, which is why the server returns every applicable warning
 * rather than one at a time. The client never decides that a warning applies, and
 * never hardcodes a rule id, which also keeps it out of reach of the API package
 * it cannot import.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapacityPeriod, CapacityPeriodWrite, Finding } from '@complan/contracts';
import {
  AVAILABILITY_FACTOR_MAX,
  AVAILABILITY_FACTOR_MIN,
  CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH,
  PRODUCTIVE_HOURS_PER_WORKDAY_MAX,
  PRODUCTIVE_HOURS_PER_WORKDAY_MIN,
} from '@complan/contracts';
import type { FieldForceRole } from '@complan/domain';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Field,
  FORM_CONTROL_CLASS,
  TextInput,
  TextArea,
} from '@/features/initiative/intake/fields';
import { DateRangeField } from '@/shared/components/DateRangeField';
import { EditorDialogActions } from '@/shared/components/EditorDialog';
import { cn } from '@/lib/utils';
import { FindingList } from '../shared/FindingList';
import { FF_ROLE_OPTIONS, ffRoleLabel } from '../ff-role-options';

/** The shared form control, filling its grid cell (the panel's fields are full-width). */
const CONTROL_CLASS = cn(FORM_CONTROL_CLASS, 'w-full');

/** Defaults for a fresh entry, from the assumptions panel of UI spec §5.3. */
const DEFAULT_PRODUCTIVE_HOURS = '4.00';
const DEFAULT_AVAILABILITY_FACTOR = '0.80';

export interface RegionOption {
  readonly id: string;
  readonly name: string;
}

/** The panel's own form state; every field is a string so inputs stay controlled. */
interface FormState {
  ffRole: string;
  regionId: string;
  /** Blank means "no headcount agreed yet", the Data_Incomplete case of R3.4. */
  headcount: string;
  workdays: string;
  oofDays: string;
  productiveHoursPerWorkday: string;
  availabilityFactor: string;
  effectivePeriodStart: string;
  effectivePeriodEnd: string;
  changeReason: string;
}

/** Which of the panel's two modes is in force — see this module's note on why there are two. */
export type CapacityPeriodPanelMode = 'create' | 'edit';

export interface CapacityPeriodPanelProps {
  /**
   * `'edit'` adjusts the figures of {@link basedOn} and locks its scope;
   * `'create'` records a new period and offers the scope as input.
   */
  readonly mode: CapacityPeriodPanelMode;
  /**
   * The version whose scope and figures seed the form, or `null` for a blank
   * entry. Never mutated: an edit is recorded as a new version (R3.3). Non-null
   * whenever {@link mode} is `'edit'` — there is nothing to edit otherwise.
   */
  readonly basedOn: CapacityPeriod | null;
  readonly regions: readonly RegionOption[];
  readonly defaultRegionId: string | undefined;
  readonly defaultFfRole: FieldForceRole;
  readonly onSubmit: (payload: CapacityPeriodWrite) => void;
  readonly isSubmitting: boolean;
  /** Findings the server returned for the last rejected submit. */
  readonly findings: readonly Finding[];
  /** Leaves an edit or a row-seeded entry for a blank one. */
  readonly onStartBlank: () => void;
}

/**
 * Whether a seed is an edit of an existing period rather than a new one.
 *
 * `basedOn !== null` is part of the condition rather than an assertion: `mode` and
 * `basedOn` are two values, and a locked scope with no record to take it from would
 * render an empty scope summary the reader could neither read nor change.
 */
function isEditSeed(
  mode: CapacityPeriodPanelMode,
  basedOn: CapacityPeriod | null,
  // A predicate on `basedOn` rather than a plain boolean, so the caller that reads
  // it keeps the narrowing the inline condition used to give: inside the true
  // branch the record is known to be there, and the read-only scope summary needs
  // that rather than a non-null assertion.
): basedOn is CapacityPeriod {
  return mode === 'edit' && basedOn !== null;
}

/**
 * The heading of the editor for a given seed, as a catalogue key.
 *
 * Three cases, and {@link EditorDialog} renders whichever this names: an edit
 * records a new version of *this* period, a copy records a *different* period
 * seeded from one, and a blank entry records a period that did not exist.
 */
export function capacityEditorTitleKey(
  mode: CapacityPeriodPanelMode,
  basedOn: CapacityPeriod | null,
): string {
  if (isEditSeed(mode, basedOn)) return 'manpower.capacityInputs.dialog.editTitle';
  return basedOn === null
    ? 'manpower.capacityInputs.dialog.createTitle'
    : 'manpower.capacityInputs.dialog.copyTitle';
}

/**
 * The note under that heading, as a catalogue key.
 *
 * The copy note is the one that has to be explicit — the figures are already filled
 * in, so nothing on screen would otherwise tell the reader that the scope is what
 * they now have to change.
 */
export function capacityEditorNoteKey(
  mode: CapacityPeriodPanelMode,
  basedOn: CapacityPeriod | null,
): string {
  if (isEditSeed(mode, basedOn)) return 'manpower.capacityInputs.dialog.editNote';
  return basedOn === null
    ? 'manpower.capacityInputs.dialog.versioningNote'
    : 'manpower.capacityInputs.dialog.copyNote';
}

function seedState(
  basedOn: CapacityPeriod | null,
  defaultRegionId: string | undefined,
  defaultFfRole: FieldForceRole,
  planningYear: number,
): FormState {
  return {
    ffRole: basedOn?.ffRole ?? defaultFfRole,
    regionId: basedOn?.regionId ?? defaultRegionId ?? '',
    headcount: basedOn?.headcount === null || basedOn === null ? '' : String(basedOn.headcount),
    workdays: basedOn === null ? '' : String(basedOn.workdays),
    oofDays: basedOn === null ? '' : String(basedOn.oofDays),
    productiveHoursPerWorkday: basedOn?.productiveHoursPerWorkday ?? DEFAULT_PRODUCTIVE_HOURS,
    availabilityFactor: basedOn?.availabilityFactor ?? DEFAULT_AVAILABILITY_FACTOR,
    effectivePeriodStart: basedOn?.effectivePeriodStart ?? `${String(planningYear)}-01-01`,
    effectivePeriodEnd: basedOn?.effectivePeriodEnd ?? `${String(planningYear)}-12-31`,
    // Always blank: the reason describes this change (R3.3).
    changeReason: '',
  };
}

export function CapacityPeriodPanel({
  mode,
  basedOn,
  regions,
  defaultRegionId,
  defaultFfRole,
  onSubmit,
  isSubmitting,
  findings,
  onStartBlank,
}: CapacityPeriodPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const planningYear = new Date().getFullYear();
  const [form, setForm] = useState<FormState>(() =>
    seedState(basedOn, defaultRegionId, defaultFfRole, planningYear),
  );

  /**
   * The warning findings the server refused the last submit with (R3.6). Their
   * rule ids are what a confirmation echoes back.
   */
  const warnings = useMemo(
    () => findings.filter((finding) => finding.tier === 'warning'),
    [findings],
  );
  const blocking = useMemo(
    () => findings.filter((finding) => finding.tier !== 'warning'),
    [findings],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  /**
   * Local pre-checks, mirroring the schema so the obvious mistakes are caught
   * before a round trip. The server still decides — this only spares the reader a
   * needless request.
   */
  const oofExceedsWorkdays =
    form.oofDays !== '' && form.workdays !== '' && Number(form.oofDays) > Number(form.workdays);
  const endBeforeStart =
    form.effectivePeriodEnd !== '' &&
    form.effectivePeriodStart !== '' &&
    form.effectivePeriodEnd < form.effectivePeriodStart;

  const complete =
    form.regionId !== '' &&
    form.workdays !== '' &&
    form.oofDays !== '' &&
    form.productiveHoursPerWorkday !== '' &&
    form.availabilityFactor !== '' &&
    form.effectivePeriodStart !== '' &&
    form.effectivePeriodEnd !== '' &&
    form.changeReason.trim().length > 0;

  const submittable = complete && !oofExceedsWorkdays && !endBeforeStart && !isSubmitting;

  const submit = (acknowledgedWarningRuleIds: readonly string[]): void => {
    onSubmit({
      ffRole: form.ffRole as FieldForceRole,
      regionId: form.regionId,
      headcount: form.headcount.trim() === '' ? null : Number(form.headcount),
      workdays: Number(form.workdays),
      oofDays: Number(form.oofDays),
      productiveHoursPerWorkday: normaliseDecimal(form.productiveHoursPerWorkday),
      availabilityFactor: normaliseDecimal(form.availabilityFactor),
      effectivePeriodStart: form.effectivePeriodStart as CapacityPeriodWrite['effectivePeriodStart'],
      effectivePeriodEnd: form.effectivePeriodEnd as CapacityPeriodWrite['effectivePeriodEnd'],
      changeReason: form.changeReason.trim(),
      acknowledgedWarningRuleIds: [...acknowledgedWarningRuleIds],
    });
  };

  const derivedCapacity = deriveCapacityPreview(form);

  /** Whether the scope is locked — see {@link isEditSeed}. */
  const isEdit = isEditSeed(mode, basedOn);

  return (
    <>
      {regions.length === 0 ? (
        // Nothing can be recorded without a Region: the scope of a period is a
        // role *and* a Region, so the panel states the prerequisite rather than
        // offering a form that could not be submitted.
        <Alert>
          <AlertTitle>{t('manpower.capacityInputs.panel.unavailableTitle')}</AlertTitle>
          <AlertDescription>{t('manpower.capacityInputs.needsRegion')}</AlertDescription>
        </Alert>
      ) : (
        <>
          {blocking.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>{t('manpower.capacityInputs.dialog.rejectedTitle')}</AlertTitle>
              <AlertDescription>
                <FindingList findings={blocking} />
              </AlertDescription>
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert className="border-[var(--warn)]">
              <AlertTitle>{t('manpower.capacityInputs.dialog.confirmTitle')}</AlertTitle>
              <AlertDescription>
                <FindingList findings={warnings} />
              </AlertDescription>
            </Alert>
          )}

          {/*
            Two fields per row wherever a field does not need the full width,
            which is what keeps the panel short enough to sit beside the register
            without the register scrolling past it.

            Pairing follows the arithmetic of R3.3 rather than mere field size,
            so the two figures a reader compares sit side by side: the day counts
            (workdays − OOF) on one row, the two rate factors (productive hours ×
            availability) on the next. Role and Region pair as the scope of the
            period. Three fields keep the whole row: the date range is a two-date
            control, the change reason is prose, and headcount carries the
            longest hint (R3.4's Data_Incomplete case), which in a half-width
            cell would wrap to four lines and cost more height than the row it
            saved.

            `mb-0` on every field is load-bearing: `Field` ships `mb-3` of its
            own, and with the grid's `gap-y-3` that spacing applied twice.
          */}
          <div className="grid grid-cols-2 items-start gap-x-3 gap-y-3">
            {/*
              In edit mode the scope is stated, not offered. R3.1 keys a
              Capacity_Period by FF role, Region and effective period, so those
              three are what identifies the record being edited — a disabled
              control would say the same thing while implying the reader is
              missing a permission, so the scope reads as context instead.
            */}
            {isEdit ? (
              <ScopeSummary period={basedOn} regions={regions} />
            ) : (
              <>
                <Field
                  label={t('manpower.capacityInputs.role')}
                  htmlFor="cp-role"
                  required
                  requiredMark="*"
                  className="mb-0"
                >
                  <select
                    id="cp-role"
                    className={CONTROL_CLASS}
                    value={form.ffRole}
                    onChange={(event) => set('ffRole', event.target.value)}
                  >
                    {FF_ROLE_OPTIONS.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label={t('manpower.capacityInputs.region')}
                  htmlFor="cp-region"
                  required
                  requiredMark="*"
                  className="mb-0"
                >
                  <select
                    id="cp-region"
                    className={CONTROL_CLASS}
                    value={form.regionId}
                    onChange={(event) => set('regionId', event.target.value)}
                  >
                    <option value="">{t('manpower.capacityInputs.dialog.selectRegion')}</option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label={`${t('manpower.capacityInputs.dialog.start')} / ${t('manpower.capacityInputs.dialog.end')}`}
                  required
                  requiredMark="*"
                  className="col-span-2 mb-0"
                  {...(endBeforeStart && {
                    hint: t('manpower.capacityInputs.dialog.endBeforeStart'),
                  })}
                >
                  <DateRangeField
                    value={
                      form.effectivePeriodStart && form.effectivePeriodEnd
                        ? {
                            startDate: form.effectivePeriodStart,
                            endDate: form.effectivePeriodEnd,
                          }
                        : null
                    }
                    onChange={(range) => {
                      set('effectivePeriodStart', range?.startDate ?? '');
                      set('effectivePeriodEnd', range?.endDate ?? '');
                    }}
                    label={t('manpower.capacityInputs.dialog.dateRangeLabel')}
                  />
                </Field>
              </>
            )}

            <Field
              label={t('manpower.capacityInputs.workdays')}
              htmlFor="cp-workdays"
              required
              requiredMark="*"
              className="mb-0"
            >
              <TextInput
                id="cp-workdays"
                type="number"
                min={0}
                step={1}
                className="w-full"
                value={form.workdays}
                onChange={(event) => set('workdays', event.target.value)}
              />
            </Field>

            <Field
              label={t('manpower.capacityInputs.oofDays')}
              htmlFor="cp-oof"
              required
              requiredMark="*"
              className="mb-0"
              {...(oofExceedsWorkdays && {
                hint: t('validation.capacityPeriod.oofDaysExceedWorkdays'),
              })}
            >
              <TextInput
                id="cp-oof"
                type="number"
                min={0}
                step={1}
                className="w-full"
                value={form.oofDays}
                onChange={(event) => set('oofDays', event.target.value)}
              />
            </Field>

            <Field
              label={t('manpower.capacityInputs.productiveHours')}
              htmlFor="cp-productive"
              required
              requiredMark="*"
              className="mb-0"
            >
              <TextInput
                id="cp-productive"
                type="number"
                min={PRODUCTIVE_HOURS_PER_WORKDAY_MIN}
                max={PRODUCTIVE_HOURS_PER_WORKDAY_MAX}
                step={0.01}
                className="w-full"
                value={form.productiveHoursPerWorkday}
                onChange={(event) => set('productiveHoursPerWorkday', event.target.value)}
              />
            </Field>

            <Field
              label={t('manpower.capacityInputs.availability')}
              htmlFor="cp-availability"
              required
              requiredMark="*"
              className="mb-0"
              hint={t('manpower.capacityInputs.dialog.availabilityHint')}
            >
              <TextInput
                id="cp-availability"
                type="number"
                min={AVAILABILITY_FACTOR_MIN}
                max={AVAILABILITY_FACTOR_MAX}
                step={0.01}
                className="w-full"
                value={form.availabilityFactor}
                onChange={(event) => set('availabilityFactor', event.target.value)}
              />
            </Field>

            <Field
              label={t('manpower.capacityInputs.headcount')}
              htmlFor="cp-headcount"
              className="col-span-2 mb-0"
              hint={t('manpower.capacityInputs.dialog.headcountHint')}
            >
              <TextInput
                id="cp-headcount"
                type="number"
                min={0}
                step={1}
                className="w-full"
                value={form.headcount}
                onChange={(event) => set('headcount', event.target.value)}
              />
            </Field>

            <Field
              label={t('manpower.capacityInputs.dialog.changeReason')}
              htmlFor="cp-reason"
              required
              requiredMark="*"
              className="col-span-2 mb-0"
              hint={t('manpower.capacityInputs.dialog.changeReasonHint')}
            >
              <TextArea
                id="cp-reason"
                maxLength={CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH}
                // Three lines is enough for the one-sentence reason R3.3 asks
                // for; the shared default is 68px and grows the panel for no
                // gain here.
                className="min-h-[52px]"
                value={form.changeReason}
                onChange={(event) => set('changeReason', event.target.value)}
              />
            </Field>
          </div>

          <p className="text-[11.5px] text-muted-foreground" aria-live="polite">
            {derivedCapacity === null
              ? t('manpower.capacityInputs.dialog.previewIncomplete')
              : t('manpower.capacityInputs.dialog.preview', {
                  value: Math.round(derivedCapacity).toLocaleString(),
                })}
          </p>

          <EditorDialogActions>
            {warnings.length > 0 ? (
              <Button
                onClick={() => submit(warnings.map((finding) => finding.ruleId))}
                disabled={!submittable}
              >
                {t('manpower.capacityInputs.dialog.confirmAndSave')}
              </Button>
            ) : (
              <Button onClick={() => submit([])} disabled={!submittable}>
                {isSubmitting ? t('admin.common.saving') : t('admin.common.save')}
              </Button>
            )}
            {/* Only meaningful once the form carries a row's figures: it is the
                way back to a blank entry. A blank form has nothing to clear.
                In edit mode it is a cancel, which is the same action under the
                name that describes what leaving an edit does. */}
            {basedOn !== null && (
              <Button variant="secondary" onClick={onStartBlank} disabled={isSubmitting}>
                {isEdit
                  ? t('manpower.capacityInputs.panel.cancelEdit')
                  : t('manpower.capacityInputs.panel.startBlank')}
              </Button>
            )}
          </EditorDialogActions>
        </>
      )}
    </>
  );
}

/**
 * The locked scope of the period being edited: FF role, Region and effective
 * period, read-only (R3.1, R3.3).
 *
 * Spans both grid columns and carries the version being edited, so the reader can
 * see which figures the form started from without leaving the panel.
 */
function ScopeSummary({
  period,
  regions,
}: {
  readonly period: CapacityPeriod;
  readonly regions: readonly RegionOption[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const regionName =
    regions.find((region) => region.id === period.regionId)?.name ?? period.regionId;

  return (
    <div className="border-line bg-bg-subtle col-span-2 rounded-[var(--radius-control)] border px-3 py-2">
      <p className="text-[10.5px] font-semibold uppercase tracking-wide text-label">
        {t('manpower.capacityInputs.dialog.editScopeHeading')}
      </p>
      <p className="mt-1 text-[12px]">
        {t('manpower.capacityInputs.dialog.editScopeValue', {
          role: ffRoleLabel(period.ffRole),
          region: regionName,
          start: period.effectivePeriodStart,
          end: period.effectivePeriodEnd,
          version: period.version,
        })}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {t('manpower.capacityInputs.dialog.editScopeHint')}
      </p>
    </div>
  );
}

/**
 * The decimal columns are `numeric(_,2)` and the contracts carry them as text to
 * avoid float drift, so a typed `4` is sent as `4.00`.
 */
function normaliseDecimal(value: string): string {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : value;
}

/** The Capacity_Hours the entry would produce (R3.3), or `null` while incomplete. */
function deriveCapacityPreview(form: FormState): number | null {
  if (form.headcount.trim() === '' || form.workdays === '' || form.oofDays === '') {
    return null;
  }
  const productiveDays = Number(form.workdays) - Number(form.oofDays);
  const hours =
    Number(form.headcount) *
    productiveDays *
    Number(form.productiveHoursPerWorkday) *
    Number(form.availabilityFactor);
  return Number.isFinite(hours) ? hours : null;
}
